import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { Button } from '@Components/common';

import styles from './SelectFundToCloneField.module.scss';
import { Loader } from '@Components/Loader/Loader';
import { StatusChip } from '@Components';
import { Fund, FundStatusEnum } from '@Types';

interface SelectFundToCloneFieldProps {
  fundsList: Fund[];
  isFundsListLoading: boolean;
  onSelect: (fund: Fund) => void;
}

export const SelectFundToCloneField = ({
  fundsList,
  isFundsListLoading,
  onSelect,
}: SelectFundToCloneFieldProps) => {
  const { t } = useTranslation('socialValue');
  const { control } = useForm({
    defaultValues: {
      selectedFund: null,
    },
  });

  const selectedFund = useWatch({ control, name: 'selectedFund' });

  if (isFundsListLoading) return <Loader />;

  return (
    <div className={styles.fundSelection}>
      <FormControl fullWidth>
        <Controller
          name="selectedFund"
          control={control}
          render={({ ref, onChange, value }) => (
            <Autocomplete
              options={fundsList}
              getOptionLabel={(option) => option.details.name || 'Unnamed fund'}
              value={value}
              onChange={(_, newValue) => onChange(newValue)}
              getOptionSelected={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={t('clone_fund_field.label')}
                  className={styles.fundSelect}
                />
              )}
              renderOption={(option) => (
                <div>
                  <Trans
                    ns="socialValue"
                    i18nKey="clone_fund_field.fund_option"
                    values={{ fundName: option.details.name || 'Unnamed fund' }}
                    components={[
                      <StatusChip
                        label={t(`dashboard:funder.fund_status.${option.status}`)}
                        color={option.status === FundStatusEnum.DRAFT ? 'secondary' : 'primary'}
                        size="small"
                        className={styles.statusChip}
                      />,
                    ]}
                  />
                </div>
              )}
            />
          )}
        />
      </FormControl>
      <Button
        buttonType="primary"
        onClick={() => {
          onSelect(selectedFund as Fund);
        }}
        disabled={!selectedFund}
        className={styles.submitButton}
        size="small"
      >
        {t('clone_fund_field.load_data')}
      </Button>
    </div>
  );
};
